import { useState, useRef, useEffect, useMemo } from 'react'
import axios from '../../../../plugins/axios'
import { useTranslation } from 'react-i18next'
import Toast from '../../../../plugins/useToast'

import Dropdown from './Dropdown'
import ChartWrapper from './ChartWrapper'
import ChartDivideLine from './ChartDivideLine'
import PrintSettingModal from './PrintSettingModal'

import { ReactComponent as IconZoomIn } from '../../../../assets/icons/ic-zoom-in.svg'
import { ReactComponent as IconZoomOut } from '../../../../assets/icons/ic-zoom-out.svg'
import backgroundImage from '../../../../assets/img/chart_bg_small.png'
import { checkEmptyValue } from '../../../../plugins/common'

const DetailView = ({ selectedData, handleView }) => {
    const { t } = useTranslation()
    const [viewType, setViewType] = useState('wave')
    const [format, setFormat] = useState({ label: '3ch(3ch*2.5s) + 1R', value: '3ch(3ch*2.5s) + 1R' })
    const [lead1, setLead1] = useState({ label: 'Ⅱ', value: 'Ⅱ' })
    const [zoom, setZoom] = useState(1)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState({ x: 0, y: 0  })
    const [isPrintSettingOpen, setIsPrintSettingOpen] = useState(false)
    const [patientData, setPatientData] = useState({})
    const [waveData, setWaveData] = useState({})
    const [metricData, setMetricData] = useState({})
    const containerRef = useRef(null)

    // const waveData = useMemo(
    //     () => Array.from({ length: 625 }, (_, i) => 50 + 30 * Math.sin(i / 20)),
    //     []
    // );
    // const waveData2 = useMemo(
    //     () => Array.from({ length: 2500 }, (_, i) => 50 + 30 * Math.cos(i / 20)),
    //     []
    // );
    const typeByLeadLabel = {
        'I': 'Ⅰ',
        'II': 'Ⅱ',
        'III': 'Ⅲ',
        'LEADⅠ': 'Ⅰ',
        'LEADⅡ': 'Ⅱ',
        'LEADⅢ': 'Ⅲ'
    }

    const metricFieldMap = {
        'P Ons': 'ponset',
        'P Dur': 'pdur',
        'QRS Ons': 'qrson',
        'QRS Dur': 'qrsdur',
        'ST80 Amp': 'st80amp',
        'Q Dur': 'qdur',
        'R Dur': 'rdur',
        'S Dur': 'sdur',
        'R\' Dur': 'r2dur', // API에 없는 필드, 기본값 처리 필요
        'S\' Dur': 's2dur', // API에 없는 필드, 기본값 처리 필요
        'R\'\' Dur': 'r3dur', // API에 없는 필드, 기본값 처리 필요
        'S\'\' Dur': 's3dur', // API에 없는 필드, 기본값 처리 필요
        'ST Dur': 'stdur',
        'T Ons': 'ton',
        'T Dur': 'tdur',
        'P+ Amp': 'pamp',
        'P- Amp': 'pnegamp', // API에 없는 필드, 기본값 처리 필요
        'P2P Amp': 'p2pamp', // API에 없는 필드, 기본값 처리 필요
        'R1 Amp': 'r1amp',
        'Q Amp': 'qamp',
        'R Amp': 'ramp',
        'S Amp': 'samp',
        'R\' Amp': 'r2amp',
        'S\' Amp': 's1amp',
        'R\'\' Amp': 'r3amp', // API에 없는 필드, 기본값 처리 필요
        'S\'\' Amp': 's2amp', // 
        'ST Amp': 'stamp',
        'STT28 Amp': 'st28amp',
        'STT38 Amp': 'st38amp',
        'T+ Amp': 'tposamp',
        'T- Amp': 'tnegamp',
        'QRS Area': 'qrsarea',
        'P Area': 'parea',
        'T Area': 'tarea',
        'P Morph': 'pmorph',
        'T Morph': 'tmorph',
        'R Notch': 'rnotches',
        'DeltaConf': 'deltaconf',
        'ST Slope': 'stslope',
        'QT Int': 'qtinterval'
    }

    const formatValueArr = [
        { label: '3ch(3ch*2.5s) + 1R', value: '3ch(3ch*2.5s) + 1R' },
        { label: '6ch(6ch*5s)', value: '6ch(6ch*5s)' },
        { label: '6ch(6ch*5s) + 1R', value: '6ch(6ch*5s) + 1R' },
    ]

    const lead1ValueArr = [
        { label: 'Ⅰ', value: 'Ⅰ' }, { label: 'Ⅱ', value: 'Ⅱ' }, { label: 'Ⅲ', value: 'Ⅲ' },
        { label: 'aVR', value: 'aVR' }, { label: 'aVL', value: 'aVL' }, { label: 'aVF', value: 'aVF' },
        { label: 'V1', value: 'V1' }, { label: 'V2', value: 'V2' }, { label: 'V3', value: 'V3' },
        { label: 'V4', value: 'V4' }, { label: 'V5', value: 'V5' }, { label: 'V6', value: 'V6' },
    ]

    const [dropdownStates, setDropdownStates] = useState({
        format: false,
        lead1: false,
    });

    const getData = async () => {
        try {
            console.log('selectedData : ',selectedData);
            const patientRes = await axios.get(`/trend/patient/info/file?fileId=${selectedData.fileId}`)
            if (patientRes.code != '200') throw patientRes 
            setPatientData(patientRes.data)
            let res = null
            switch(viewType) {
                case 'wave':
                    res = await axios.get(`/trend/waveforms/file?fileId=${selectedData.fileId}`)
                    if (res.code != '200') throw res
                    const newWaveData = {}
                    res.data.waveformData.leads.forEach(item => {
                        newWaveData[typeByLeadLabel[item.leadLabel] || item.leadLabel] = {
                            ...item,
                            wave: item.vals.slice(0, 5000)
                        }
                    })
                    setWaveData(newWaveData)
                    break;
                case 'value':
                    res = await axios.get(`/trend/metric/file?fileId=${selectedData.fileId}`)
                    if (res.code != '200') throw res
                    const newMetricData = {}
                    res.data.leadMetric.forEach(item => {
                        newMetricData[typeByLeadLabel[item.lead] || item.lead] = item
                    })
                    setMetricData(newMetricData)
                    break;
                default:
                    break;
            }
        } catch (err) {
            console.warn(err)
        }
    }

    const handleClickDropdown = (type, val) => {
        setDropdownStates(prev => ({
          ...prev,
          [type]: typeof val !== 'undefined' ? false : !prev[type],
        }));
    };

    const handleZoom = (direction) => {
        if (viewType !== 'wave') return
        // Max zoom is 6x, min zoom is 1x
        if (direction === 'in') {
            setZoom(prevZoom => {
                const newZoom = Math.min(prevZoom * 1.2, 6);
                
                // 줌 인 후 경계 체크 및 위치 조정 (중앙점 기준)
                if (containerRef.current) {
                    const container = containerRef.current;
                    const containerRect = container.getBoundingClientRect();
                    
                    // 컨테이너 중앙점
                    const centerX = containerRect.width / 2;
                    const centerY = containerRect.height / 2;
                    
                    // 줌 비율
                    const zoomRatio = newZoom / prevZoom;
                    
                    setPosition(prevPosition => {
                        // 중앙점을 기준으로 새로운 위치 계산
                        const newX = centerX - (centerX - prevPosition.x) * zoomRatio;
                        const newY = centerY - (centerY - prevPosition.y) * zoomRatio;
                        
                        // 경계 제한
                        const scaledWidth = containerRect.width * newZoom;
                        const scaledHeight = containerRect.height * newZoom;
                        
                        const maxX = 0;
                        const minX = containerRect.width - scaledWidth;
                        const maxY = 0;
                        const minY = containerRect.height - scaledHeight;
                        
                        return {
                            x: Math.max(minX, Math.min(maxX, newX)),
                            y: Math.max(minY, Math.min(maxY, newY))
                        };
                    });
                }
                
                return newZoom;
            });
        } else if (direction === 'out') {
            setZoom(prevZoom => {
                const newZoom = Math.max(prevZoom / 1.2, 1);
                
                if (newZoom <= 1) {
                    setPosition({ x: 0, y: 0 });
                } else {
                    // 줌 아웃 후 경계 체크 및 위치 조정 (중앙점 기준)
                    if (containerRef.current) {
                        const container = containerRef.current;
                        const containerRect = container.getBoundingClientRect();
                        
                        // 컨테이너 중앙점
                        const centerX = containerRect.width / 2;
                        const centerY = containerRect.height / 2;
                        
                        // 줌 비율
                        const zoomRatio = newZoom / prevZoom;
                        
                        setPosition(prevPosition => {
                            // 중앙점을 기준으로 새로운 위치 계산
                            const newX = centerX - (centerX - prevPosition.x) * zoomRatio;
                            const newY = centerY - (centerY - prevPosition.y) * zoomRatio;
                            
                            // 경계 제한
                            const scaledWidth = containerRect.width * newZoom;
                            const scaledHeight = containerRect.height * newZoom;
                            
                            const maxX = 0;
                            const minX = containerRect.width - scaledWidth;
                            const maxY = 0;
                            const minY = containerRect.height - scaledHeight;
                            
                            return {
                                x: Math.max(minX, Math.min(maxX, newX)),
                                y: Math.max(minY, Math.min(maxY, newY))
                            };
                        });
                    }
                }
                
                return newZoom;
            });
        }
    }

    const handleMouseDown = (e) => {
        if (viewType !== 'wave') return
        if (zoom > 1) {
            setIsDragging(true)
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            })
        }
    }

    const handleMouseMove = (e) => {
        if (viewType !== 'wave') return
        if (isDragging && zoom > 1 && containerRef.current) {
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            
            const newX = e.clientX - dragStart.x;
            const newY = e.clientY - dragStart.y;
            
            // 확대된 컨텐츠의 실제 크기
            const scaledWidth = containerRect.width * zoom;
            const scaledHeight = containerRect.height * zoom;
            
            // 경계 계산
            const maxX = 0; // 우측/상단 경계 (원래 위치)
            const minX = containerRect.width - scaledWidth; // 좌측 경계
            const maxY = 0; // 상단 경계
            const minY = containerRect.height - scaledHeight; // 하단 경계
            
            // 경계 내에서만 이동하도록 제한
            const constrainedX = Math.max(minX, Math.min(maxX, newX));
            const constrainedY = Math.max(minY, Math.min(maxY, newY));
            
            setPosition({
                x: constrainedX,
                y: constrainedY
            });
        }
    }

    const handleMouseUp = () => {
        if (viewType !== 'wave') return
        setIsDragging(false)
    }

    //rodney - print lead handler 
    const handleLeadPrint = (viewType,formatValue,lead1Value) => {
        setIsPrintSettingOpen(false)
        const params = {
            createdAt: selectedData?.createdAt || '',
            fileId: selectedData?.fileId || '',
            type: viewType || '',
            format: formatValue?.value || '',
            lead: lead1Value?.value || '',            
        }        

        const windowFeatures = "width=1200,height=800,left=100,top=100,scrollbars=yes,resizable=yes";        
        const setUrl = `${window.location.origin}/print/leadform?params=${encodeURIComponent(JSON.stringify(params))}`;
        const newWindow = window.open(setUrl, "popupWindow", windowFeatures);
        if (newWindow) {
            newWindow.focus();
        } else {
            Toast({ isSuccess: false, message: t('error.failOpenPage') })
        }
    }

    useEffect(() => {
        setZoom(1)
        setPosition({ x: 0, y: 0 })
    }, [format, lead1, viewType])

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheelEvent = (e) => {
            if (viewType !== 'wave') return;
            
            e.preventDefault(); // 이제 preventDefault가 작동합니다
            
            const delta = e.deltaY;
            const direction = delta > 0 ? 'out' : 'in';
            
            // 마우스 위치를 기준으로 줌
            const containerRect = container.getBoundingClientRect();
            const mouseX = e.clientX - containerRect.left;
            const mouseY = e.clientY - containerRect.top;
            
            if (direction === 'in') {
                setZoom(prevZoom => {
                    const newZoom = Math.min(prevZoom * 1.1, 6);
                    
                    // 마우스 위치를 기준으로 줌 인
                    const zoomRatio = newZoom / prevZoom;
                    
                    setPosition(prevPosition => {
                        // 마우스 위치를 기준으로 새로운 위치 계산
                        const newX = mouseX - (mouseX - prevPosition.x) * zoomRatio;
                        const newY = mouseY - (mouseY - prevPosition.y) * zoomRatio;
                        
                        // 경계 제한
                        const scaledWidth = containerRect.width * newZoom;
                        const scaledHeight = containerRect.height * newZoom;
                        
                        const maxX = 0;
                        const minX = containerRect.width - scaledWidth;
                        const maxY = 0;
                        const minY = containerRect.height - scaledHeight;
                        
                        return {
                            x: Math.max(minX, Math.min(maxX, newX)),
                            y: Math.max(minY, Math.min(maxY, newY))
                        };
                    });
                    
                    return newZoom;
                });
            } else if (direction === 'out') {
                setZoom(prevZoom => {
                    const newZoom = Math.max(prevZoom / 1.1, 1);
                    
                    if (newZoom <= 1) {
                        setPosition({ x: 0, y: 0 });
                    } else {
                        // 마우스 위치를 기준으로 줌 아웃
                        const zoomRatio = newZoom / prevZoom;
                        
                        setPosition(prevPosition => {
                            // 마우스 위치를 기준으로 새로운 위치 계산
                            const newX = mouseX - (mouseX - prevPosition.x) * zoomRatio;
                            const newY = mouseY - (mouseY - prevPosition.y) * zoomRatio;
                            
                            // 경계 제한
                            const scaledWidth = containerRect.width * newZoom;
                            const scaledHeight = containerRect.height * newZoom;
                            
                            const maxX = 0;
                            const minX = containerRect.width - scaledWidth;
                            const maxY = 0;
                            const minY = containerRect.height - scaledHeight;
                            
                            return {
                                x: Math.max(minX, Math.min(maxX, newX)),
                                y: Math.max(minY, Math.min(maxY, newY))
                            };
                        });
                    }
                    
                    return newZoom;
                });
            }
        };

        // passive: false로 이벤트 리스너 등록
        container.addEventListener('wheel', handleWheelEvent, { passive: false });

        if (selectedData) {
            getData()
        }

        return () => {
            container.removeEventListener('wheel', handleWheelEvent);
        };
    }, [viewType]); // viewType이 변경될 때마다 이벤트 리스너 재등록

    return (
        <>
            <div className="w-[254px] bg-[#D7D7D7] flex flex-col gap-[24px]" style={{ padding: 20, paddingRight: 14, border: '1px solid #CECECE' }}>
                <div className="flex flex-col gap-[6px]">
                    <div className="text-[#444444]">{t('leadModal.viewType')}</div>
                    <div className="flex items-center gap-[20px]">
                        <label className="custom-radio">
                            <input
                                type="radio"
                                checked={viewType === 'wave'}
                                onChange={() => setViewType('wave')}
                            />
                            <span className="font-light" style={{ color: 'black', paddingLeft: 25, fontSize: 16 }}>{t('leadModal.wave')}</span>
                        </label>
                        <label className="custom-radio">
                            <input
                                type="radio"
                                checked={viewType === 'value'}
                                onChange={() => setViewType('value')}
                            />
                            <span className="font-light" style={{ color: 'black', paddingLeft: 25, fontSize: 16 }}>{t('leadModal.values')}</span>
                        </label>
                    </div>
                </div>
                <div className="flex flex-col gap-[6px]" style={{ opacity: viewType === 'value' ? 0.5 : 1 }}>
                    <div className="text-[#444444]">{t('leadModal.format')}</div>
                    <Dropdown
                        customWidth={220}
                        type={'view-format'}
                        isShow={dropdownStates.format}
                        value={format}
                        valueArr={formatValueArr}
                        handleClick={(val) => { if (viewType !== 'value') handleClickDropdown('format', val) }}
                        handleValueClick={(val) => { setFormat(val) }}
                    />
                </div>
                <div className="flex flex-col gap-[6px]" style={{ opacity: (viewType === 'value' || format.value === '6ch(6ch*5s)') ? 0.5 : 1 }}>
                    <div className="text-[#444444]">{t('leadModal.rhythmLead1')}</div>
                    <Dropdown
                        customWidth={220}
                        type={'view-lead-1'}
                        isShow={dropdownStates.lead1}
                        value={lead1}
                        valueArr={lead1ValueArr}
                        handleClick={(val) => { if (viewType !== 'value' && format.value !== '6ch(6ch*5s)') handleClickDropdown('lead1', val) }}
                        handleValueClick={(val) => { setLead1(val) }}
                    />
                </div>
                <div className="flex flex-col gap-[6px]">
                    <div className="text-[#444444]">{t('leadModal.print')}</div>
                    <div onClick={() => { setIsPrintSettingOpen(true) }} className="flex justify-center cursor-pointer items-center w-full h-[32px] rounded-[6px] bg-gradient-to-b from-[#5E5E5E] to-[#383838] text-white font-light" style={{ fontSize: 16, border: '1px solid #5E5E5E' }}>{t('leadModal.print')}</div>
                </div>
            </div>
            <div className="flex-1 flex flex-col gap-[6px]" style={{ width: 'calc(100% - 254px)', paddingTop: 20, paddingLeft: 20 }}>
                <div className="" style={{ fontSize: 18 }}>{checkEmptyValue(selectedData?.eventDt)}</div>
                <div className="flex flex-col gap-[8px] py-1 px-3 rounded-[6px] bg-[#CECECE]">
                    <div className="flex gap-[20px]">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.patientId')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{checkEmptyValue(patientData.devicePatientId)}</div>
                            </div>
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.name')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{checkEmptyValue(patientData.patientName)}</div>
                            </div>
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.dateOfBirth')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{patientData.birthDay === 'none' ? '-' : checkEmptyValue(patientData.birthDay)}</div>
                            </div>
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.gender')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{patientData.gender === 'N' ? '-' : patientData.gender === 'M' ? 'Male' : 'Female'}</div>
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.ventRate')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{`${checkEmptyValue(patientData.ventrate)} bpm`}</div>
                            </div>
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.qrsDuration')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{`${checkEmptyValue(patientData.qrsms)} ms`}</div>
                            </div>
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.qtqtc')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{`${checkEmptyValue(patientData.qtms)}/${checkEmptyValue(patientData.qtcms)} ms`}</div>
                            </div>
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.ptqtInterval')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{`${checkEmptyValue(patientData.prms)}/${checkEmptyValue(patientData.qtms)} ms`}</div>
                            </div>
                            <div className="flex items-center gap-[2px]">
                                <div className="flex justify-between font-light opacity-70 text-[#2C2C2C]" style={{ minWidth: 146, maxWidth: 146 }}>
                                    {t('leadModal.pqrstAxis')}
                                    <div className="" style={{ minWidth: 4, maxWidth: 4 }}>:</div>
                                </div>
                                <div className="font-light text-[#1F1F1F]" style={{ minWidth: 181, maxWidth: 181 }}>{`${checkEmptyValue(patientData.paxis)} / ${checkEmptyValue(patientData.qrsaxis)} / ${checkEmptyValue(patientData.taxis)} deg`}</div>
                            </div>
                        </div>
                    </div>
                    <div className="font-light"></div>
                </div>
                <div
                    ref={containerRef}
                    className="mt-[10px] rounded-[6px] w-full relative overflow-hidden"
                    style={{ 
                        height: 'calc(100% - 229px)',
                        cursor: viewType === 'wave' ? (zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default') : 'default',
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {
                        viewType === 'wave' &&
                        <>
                            <div 
                                className="w-full h-full flex flex-col relative"
                                style={{
                                    paddingTop: 8,
                                    paddingBottom: 24,
                                    backgroundImage: `url(${backgroundImage})`,
                                    backgroundRepeat: 'repeat',
                                    imageRendering: 'pixelated', // 보간 제거
                                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                                    transformOrigin: 'top left',
                                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                                }}
                            >
                                {
                                    format.value === '3ch(3ch*2.5s) + 1R' &&
                                    <>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅰ'} chartData={waveData['Ⅰ']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'aVR'} chartData={waveData['aVR']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V1'} chartData={waveData['V1']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V4'} chartData={waveData['V4']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅱ'} chartData={waveData['Ⅱ']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'aVL'} chartData={waveData['aVL']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V2'} chartData={waveData['V2']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V5'} chartData={waveData['V5']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅲ'} chartData={waveData['Ⅲ']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'aVF'} chartData={waveData['aVF']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V3'} chartData={waveData['V3']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V6'} chartData={waveData['V6']?.wave} chartWidth={286.5} chartHeight={96.5} totalSeconds={2.5} />
                                        </div>
                                        <ChartWrapper type={lead1.value} chartData={waveData[lead1.value]?.wave} chartWidth={1146} chartHeight={96.5} totalSeconds={10} />
                                        <div className="absolute bottom-0 text-[#3DFF3D]" style={{ fontFamily: 'Roboto' }}>
                                            <span className="ml-2">60Hz</span>
                                            <span className="ml-8">0.05Hz ~ 40Hz</span>
                                            <span className="ml-8">10mm/mV </span>
                                            <span className="ml-8">25mm/sec</span>
                                            <span className="ml-8">3*4 1R</span>
                                        </div>
                                    </>
                                }
                                {
                                    format.value === '6ch(6ch*5s)' &&
                                    <>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅰ'} chartData={waveData['Ⅰ']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V1'} chartData={waveData['V1']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅱ'} chartData={waveData['Ⅱ']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V2'} chartData={waveData['V2']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅲ'} chartData={waveData['Ⅲ']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V3'} chartData={waveData['V3']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'aVR'} chartData={waveData['aVR']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V4'} chartData={waveData['V4']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'aVL'} chartData={waveData['aVL']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V5'} chartData={waveData['V5']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1">
                                            <ChartWrapper type={'aVF'} chartData={waveData['aVF']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V6'} chartData={waveData['V6']?.wave} chartWidth={573} chartHeight={57.7} totalSeconds={5} />
                                        </div>
                                        <div className="absolute bottom-0 text-[#3DFF3D]" style={{ fontFamily: 'Roboto' }}>
                                            <span className="ml-2">60Hz</span>
                                            <span className="ml-8">0.05Hz ~ 40Hz</span>
                                            <span className="ml-8">10mm/mV </span>
                                            <span className="ml-8">25mm/sec</span>
                                            <span className="ml-8">2*6</span>
                                        </div>
                                    </>
                                }
                                {
                                    format.value === '6ch(6ch*5s) + 1R' &&
                                    <>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅰ'} chartData={waveData['Ⅰ']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V1'} chartData={waveData['V1']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅱ'} chartData={waveData['Ⅱ']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V2'} chartData={waveData['V2']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'Ⅲ'} chartData={waveData['Ⅲ']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V3'} chartData={waveData['V3']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'aVR'} chartData={waveData['aVR']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V4'} chartData={waveData['V4']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'aVL'} chartData={waveData['aVL']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V5'} chartData={waveData['V5']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                        </div>
                                        <div className="flex w-full flex-1 mb-5">
                                            <ChartWrapper type={'aVF'} chartData={waveData['aVF']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                            <ChartDivideLine />
                                            <ChartWrapper type={'V6'} chartData={waveData['V6']?.wave} chartWidth={573} chartHeight={46.6} totalSeconds={5} />
                                        </div>
                                        <ChartWrapper type={lead1.value} chartData={waveData[lead1.value]?.wave} chartWidth={1146} chartHeight={46.6} totalSeconds={10} />
                                        <div className="absolute bottom-0 text-[#3DFF3D]" style={{ fontFamily: 'Roboto' }}>
                                            <span className="ml-2">60Hz</span>
                                            <span className="ml-8">0.05Hz ~ 40Hz</span>
                                            <span className="ml-8">10mm/mV </span>
                                            <span className="ml-8">25mm/sec</span>
                                            <span className="ml-8">2*6 1R</span>
                                        </div>
                                    </>
                                }
                            </div>
                            <div className="absolute bottom-[6px] right-[6px] w-[34px] h-[69px] rounded-[888px] flex flex-col bg-[#1F1F1FE5]">
                                <div onClick={() => { handleZoom('in') }} className="flex-1 flex items-center justify-center cursor-pointer">
                                    <IconZoomIn className="w-[20px] h-[20px] text-[#ffffff]" />
                                </div>
                                <div className="h-[1px] w-full bg-white opacity-30" />
                                <div onClick={() => { handleZoom('out') }} className="flex-1 flex items-center justify-center cursor-pointer">
                                    <IconZoomOut className="w-[20px] h-[20px] text-[#ffffff]" />
                                </div>
                            </div>
                        </>
                    }
                    {
                        viewType === 'value' &&
                        <div className="flex h-full">
                            <div className="flex flex-col">
                                {
                                    ['', 'LEADⅠ', 'LEADⅡ', 'LEADⅢ', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'].map((lead, index) => {
                                        return (
                                            <div 
                                                className="flex-1 flex items-center h-[32px] bg-[#262626] font-light text-[#CECECE]" 
                                                style={{ 
                                                    paddingLeft: 8,
                                                    paddingBottom: index === 12 ? 6 : 0,
                                                    border: '1px solid #FFFFFF',
                                                    borderTopWidth: 0,
                                                    borderLeftWidth: 0,
                                                    borderBottomWidth: index === 12 ? 0 : 1,
                                                    borderRightWidth: index === 0 ? 1 : 0,
                                                    minWidth: 70,
                                                    maxWidth: 70
                                                }} key={`table-lead-type-${lead}`}>{lead}</div>
                                        )
                                    })
                                }
                            </div>
                            <div className="flex scrollbar scrollbar--gray gap-[1px]">
                                {
                                    ['P Ons', 'P Dur', 'QRS Ons', 'QRS Dur', 'ST80 Amp', 'Q Dur', 'R Dur', 'S Dur', 'R’ Dur', 'S’ Dur', 'R’’ Dur', 'S’’ Dur', 'ST Dur', 'T Ons', 'T Dur', 'P+ Amp', 'P- Amp', 'P2P Amp', 'R1 Amp', 'Q Amp', 'R Amp', 'S Amp', 'R’ Amp', 'S’ Amp', 'R’’ Amp', 'S’’ Amp', 'ST Amp', 'STT28 Amp', 'STT38 Amp', 'T+ Amp', 'T- Amp', 'QRS Area', 'P Area', 'T Area', 'P Morph', 'T Morph', 'R Notch', 'DeltaConf', 'ST Slope', 'QT Int'].map((value, index) => {
                                        return (
                                            <div className="flex flex-col flex-shrink-0 gap-[1px]" style={{ minWidth: 60, width: 'auto' }} key={`table-value-type-${value}`}>
                                                <div
                                                    style={{
                                                        paddingLeft: 8,
                                                        paddingRight: 8,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    className="flex items-center h-[32px] bg-[#262626] font-light text-[#CECECE]"
                                                >
                                                        {value}
                                                </div>
                                                {
                                                    ['LEADⅠ', 'LEADⅡ', 'LEADⅢ', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'].map((lead, index) => {
                                                        return (
                                                            <div className="flex-1 flex items-center justify-center h-[32px] bg-white font-light" key={`table-lead-value-${lead}`}>{checkEmptyValue(metricData[typeByLeadLabel[lead] || lead] ? metricData[typeByLeadLabel[lead] || lead][metricFieldMap[value]] : null)}</div>
                                                        )
                                                    })
                                                }
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
            {
                isPrintSettingOpen &&
                <PrintSettingModal 
                    handleClose={() => { setIsPrintSettingOpen(false) }}
                    handleLeadPrint={handleLeadPrint}
                />
            }
        </>
    )
}

export default DetailView